import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {useAuth, useToast, useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {useEffect} from 'react';

import {
  IMetroAddFavoriteProduct,
  IMetroAddFavoriteProductIdsResponse,
  IMetroAddFavoriteProductRequest,
  IMetroAddFavoriteProductResponse,
  IMetroCategories,
  IMetroProductDetailsResponse,
  IMetroProductsRequest,
  IMetroProductsResponse,
  IMetroSearchResponse,
} from '../types';
import {IMetroFiltersResponse} from '../types/filters';
import {
  addMetroFavoriteProduct,
  deleteMetroFavoriteProduct,
  getMetroFavoriteProduct,
  getMetroFavoriteProductIds,
  getMetroFilters,
  getMetroMainCategories,
  getMetroProductDetails,
  getMetroProducts,
  getMetroPromo,
  getMetroPromoFilters,
  getMetroSearch,
  getMetroSearchFilters,
  getMetroSearchPage,
} from '.';

export const useMetroMainCategories = (queryKey: QueryKey, options?: UseQueryOptions<IMetroCategories>) => {
  const {locale} = useRouter();

  return useQuery<IMetroCategories>({
    queryKey: [...queryKey, locale],
    queryFn: () => getMetroMainCategories(),
    ...options,
  });
};

export const useInfiniteMetroProducts = (
  params: IMetroProductsRequest,
  options?: UseInfiniteQueryOptions<IMetroProductsResponse>,
) => {
  const {locale, query} = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {slug, ...restQuery} = query;

  return useInfiniteQuery<IMetroProductsResponse>({
    queryKey: ['metroProductsInfinite', params.slug, locale, restQuery],
    queryFn: ({pageParam = 0}) =>
      getMetroProducts({...params, offset: pageParam, filters: restQuery as Record<string, any>}),
    staleTime: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage?.next && typeof lastPage?.next === 'string') {
        return parseInt(lastPage.next.split('offset=')[1]);
      }
      return null;
    },
    ...options,
  });
};

export const useMetroSearch = (query: string, options?: UseQueryOptions<IMetroSearchResponse>) => {
  const {locale} = useRouter();

  return useQuery<IMetroSearchResponse>({
    queryKey: ['metroSearch', locale, query],
    queryFn: () => getMetroSearch(query),
    ...options,
  });
};

export const useInfiniteMetroSearchProducts = (
  params: IMetroProductsRequest,
  options?: UseInfiniteQueryOptions<IMetroProductsResponse>,
) => {
  const {locale, query} = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {slug, ...restQuery} = query;

  return useInfiniteQuery<IMetroProductsResponse>({
    queryKey: ['metroProductsInfinite', params.slug, locale, restQuery],
    queryFn: ({pageParam = 0}) =>
      getMetroSearchPage({...params, offset: pageParam, filters: restQuery as Record<string, any>}),
    staleTime: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage?.next && typeof lastPage?.next === 'string') {
        return parseInt(lastPage.next.split('offset=')[1]);
      }
      return null;
    },
    ...options,
  });
};

export const useMetroFilters = (query: string, options?: UseQueryOptions<IMetroFiltersResponse>) => {
  const {locale} = useRouter();

  return useQuery<IMetroFiltersResponse>({
    queryKey: ['metroFilters', locale, query],
    queryFn: () => getMetroFilters(query),
    ...options,
  });
};

export const useMetroSearchFilters = (query: string, options?: UseQueryOptions<IMetroFiltersResponse>) => {
  const {locale} = useRouter();

  return useQuery<IMetroFiltersResponse>({
    queryKey: ['metroSearchFilters', locale, query],
    queryFn: () => getMetroSearchFilters(query),
    ...options,
  });
};

export const useMetroPromoFilters = (options?: UseQueryOptions<IMetroFiltersResponse>) => {
  const {locale} = useRouter();

  return useQuery<IMetroFiltersResponse>({
    queryKey: ['metroPromoFilters', locale],
    queryFn: () => getMetroPromoFilters(),
    ...options,
  });
};

export const useMetroProductDetails = (slug: string, options?: UseQueryOptions<IMetroProductDetailsResponse>) => {
  const {locale} = useRouter();

  return useQuery<IMetroProductDetailsResponse>({
    queryKey: ['metroProductDetails', locale, slug],
    queryFn: () => getMetroProductDetails(slug),
    ...options,
  });
};

export const useInfiniteMetroPromo = (
  {limit = 20}: {limit?: number} = {},
  options?: UseInfiniteQueryOptions<IMetroProductsResponse>,
) => {
  const {locale, query} = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {slug, promo, ...restQuery} = query;
  return useInfiniteQuery<IMetroProductsResponse>({
    queryKey: ['metroPromoInfinite', locale, restQuery, limit ?? 'default'],
    queryFn: ({pageParam = 0}) => getMetroPromo({offset: pageParam, limit, filters: restQuery as Record<string, any>}),
    staleTime: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage?.next && typeof lastPage?.next === 'string') {
        return parseInt(lastPage.next.split('offset=')[1]);
      }
      return null;
    },
    ...options,
  });
};

export const useMetroAddFavoriteProduct = (
  options?: UseMutationOptions<IMetroAddFavoriteProductResponse, unknown, IMetroAddFavoriteProductRequest>,
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const {t} = useTranslation();

  return useMutation<IMetroAddFavoriteProductResponse, unknown, IMetroAddFavoriteProductRequest>({
    mutationFn: addMetroFavoriteProduct,
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['metroFavoriteIds']});
      options?.onSuccess?.(data, variables, context);
      toast.success(t('FAVOURITE_ADD'));
    },
    onError: () => {
      toast.error(t('ERROR_FAVOURITE_ADD'));
    },
  });
};

export const useMetroAddFavoriteProductGuarded = (
  options?: UseMutationOptions<IMetroAddFavoriteProductResponse, unknown, IMetroAddFavoriteProductRequest>,
) => {
  const {isAuthenticated, redirectToLoginPage} = require('hooks').useAuth();
  const {asPath} = require('next/router').useRouter();

  const base = useMetroAddFavoriteProduct(options);

  return {
    ...base,
    mutate: (variables: IMetroAddFavoriteProductRequest, mutateOptions?: any) => {
      if (!isAuthenticated) {
        redirectToLoginPage({redirectTo: asPath});
        return;
      }
      base.mutate(variables, mutateOptions);
    },
    mutateAsync: async (variables: IMetroAddFavoriteProductRequest, mutateOptions?: any) => {
      if (!isAuthenticated) {
        redirectToLoginPage({redirectTo: asPath});
        return Promise.resolve(undefined as unknown as IMetroAddFavoriteProductResponse);
      }
      return base.mutateAsync(variables, mutateOptions);
    },
  } as typeof base;
};

export const useMetroFavoriteProducts = (options?: UseQueryOptions<IMetroAddFavoriteProduct[]>) => {
  const {locale, asPath} = useRouter();
  const {isAuthenticated, redirectToLoginPage} = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      redirectToLoginPage({redirectTo: asPath});
    }
  }, [isAuthenticated, redirectToLoginPage, asPath]);

  return useQuery<IMetroAddFavoriteProduct[]>({
    queryKey: ['metroFavorites', locale],
    queryFn: () => getMetroFavoriteProduct(),
    enabled: isAuthenticated,
    ...options,
  });
};

export const useMetroFavoriteProductIds = (options?: UseQueryOptions<IMetroAddFavoriteProductIdsResponse>) => {
  const {locale} = useRouter();
  const {isAuthenticated} = useAuth();

  return useQuery<IMetroAddFavoriteProductIdsResponse>({
    queryKey: ['metroFavoriteIds', locale],
    queryFn: () => getMetroFavoriteProductIds(),
    enabled: isAuthenticated,
    ...options,
  });
};

export const useMetroDeleteFavoriteProduct = (
  options?: UseMutationOptions<IMetroAddFavoriteProductResponse, unknown, IMetroAddFavoriteProductRequest>,
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const {t} = useTranslation();

  return useMutation<IMetroAddFavoriteProductResponse, unknown, IMetroAddFavoriteProductRequest>({
    mutationFn: deleteMetroFavoriteProduct,
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: ['metroFavoriteIds']});
      options?.onSuccess?.(data, variables, context);
      toast.success(t('FAVOURITE_REMOVE'));
    },
    onError: () => {
      toast.error(t('ERROR_FAVOURITE_REMOVE'));
    },
  });
};
